import React from "react";
import { RootRoute } from "@app/const";
import { useCanvasTabs } from "@app/hooks";
import { UsedInPanels } from "@app/page-components/shared";
import {
    createUuid,
    navigateToRoute,
    waitForCanvas
} from "@app/utilities";
import FunctionsUsedIn from "@app/utilities/usedIn/FunctionsUsedIn";
import IntentUsedIn from "@app/utilities/usedIn/IntentUsedIn";
import LocalVariablesUsedIn from "@app/utilities/usedIn/LocalVariableUsedIn";
import ModuleUsedIn from "@app/utilities/usedIn/ModuleUsedIn";
import ObjectsUsedIn from "@app/utilities/usedIn/ObjectsUsedIn";
import VariablesUsedIn from "@app/utilities/usedIn/VariablesUsedIn";

/**
 * JSDoc imports.
 *
 * @import { DesignModule } from '@app/types';
 */

export default class UsedInDialog extends React.Component {
    /**
     * Dialog for used in reference check
     * @module UsedInDialog
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);
    }

    /**
     * Open and jump to module tab
     * @param {DesignModule} mod Serialized module node model
     */
    async openCanvas(mod) {
        w2popup.close();
        navigateToRoute(RootRoute.canvas);
        useCanvasTabs.getState().addTab(mod.id);

        // select component in module flow
        const { nodes } = mod.versions['1'].flow;
        const matchedNode = nodes.find(n => n.props.parentComponentID === this.props.node.id);
        if (!matchedNode) {
            return;
        }
        await waitForCanvas(mod.id);
        window.apm.postEventToCurrentView({
            target: "request-select-matching",
            event: { id: matchedNode.id, type: matchedNode.type }
        });
    }

    // MODULES --
    getModuleUsedInPanel(key, usedIn, components){
        return (
            <div style={{padding: '1.2em', borderRadius: 'var(--radius-small)', border: '1px solid var(--theme-text-emphasis)', marginBottom: '1.2em'}} key={createUuid()}>
                <h4 style={{marginTop: '0'}}>{this.getModuleUsedInHeader(key)}</h4>
                {this.getModuleUsedInList(key, usedIn, components)}
            </div>
        )
    }

    getModuleUsedInHeader(headerKey) {
        switch (headerKey) {
            case 'usedInFlows':
                return "Modules";
            case 'usedInObjects':
                return 'Application Objects'
            case 'usedInExitNodes':
                return "Exit Nodes";
            case 'usedInGlobalIntents':
                return "Global Intents";
            case 'usedInGlobalErrorHandlig':
                return "Global Error Handlig (Settings Page)";
            case 'usedInGlobalExceptionHandlig':
                return "Global Exception Handlig (Settings Page)";
            case 'usedInModuleInputs':
                return "Module Inputs";
            case 'usedInLocalVariables':
                return "Local Variables";
        }

        return '';
    }

    // TODO: make sorted lists
    getModuleUsedInList(key, usedIn, serialized){
        const lists = [];
        const {components} = serialized;
        if(key === 'usedInFlows'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn.map((flow, idx) => {  
                        const moduleIsDisbaled = components.modules.find(mod => mod.id === flow).props.config.disabledInFlow; 
                        return (
                            <li key={'flow' + idx} style={{padding: '4px 0'}}>
                                <div><a style={{color: (moduleIsDisbaled) ? 'var(--theme-text-muted)': ''}} onClick={() => this.openCanvas(components.modules.find(mod => mod.id === flow))}><span style={{paddingRight: '4px'}}><i className="fe icon-package"></i></span>{components.modules.find(mod => mod.id === flow).name}</a></div>
                            </li>
                        )
                    })}
                </ul>
            );
        }

        if (key === 'usedInObjects') {
            return this.getUsedInObjectsList(usedIn[key], serialized.components);
        }

        if(key === 'usedInExitNodes'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((exit, idx) => {   
                        return (
                            <li key={'exit' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}><i className="fe icon-log-out"></i></span>
                                    {exit.exitNode.name}
                                    
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'usedInModuleInputs'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((input, idx) => {   
                        return (
                            <li key={'input' + idx} style={{padding: '4px 0'}}>
                                <div style={{ padding: '2px 0' }} key={createUuid()}>
                                    <i className={'fe icon-package'} style={{ padding: '0 4px' }}></i>
                                    <a onClick={() => this.openCanvas(input.cm)}><span style={{ textDecoration: 'underline' }} />{`${input.node.name} in ${input.cm.name} (Module)`}</a>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'usedInLocalVariables'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((localVar, idx) => {   
                        return (
                            <li key={'localVar' + idx} style={{padding: '4px 0'}}>
                                <div style={{ padding: '2px 0' }} key={createUuid()}>
                                    <i className={'fe icon-hash'} style={{ padding: '0 4px' }}></i>
                                    <a onClick={() => this.openCanvas(localVar.cm)}><span style={{ textDecoration: 'underline' }} />{`${localVar.lv.name} in ${localVar.cm.name} (Module)`}</a>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        
        if(key === 'usedInGlobalIntents'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((globalIntent, idx) => {   
                        var conditionName = globalIntent.conditionProperties.props.config.name ? globalIntent.conditionProperties.props.config.name: globalIntent.conditionProperties.props.config.serializedCondition;
                        var intentName = components.intents.find(int => int.id === globalIntent.intent).name;
                        if (conditionName !== "") {
                            return (
                            <li key={'globalIntent' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}>
                                    <i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i></span>{`${intentName} + ${conditionName}`}</div>
                            </li>
                            )
                        }
                        else {
                            return (
                                <li key={'globalIntent' + idx} style={{padding: '4px 0'}}>
                                    <div>
                                        <span style={{paddingRight: '4px'}}>
                                        <i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i></span>{`${intentName}`}</div>
                                </li>
                            )
                        }
                    })}
                </ul>
            );
           
        }

        if(key === 'usedInGlobalErrorHandlig'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((globalError, idx) => {   
                        return (
                            <li key={'globalError' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}>
                                        </span>{globalError.name}</div>
                            </li>
                        )
                    })}
                </ul>
            );
        }

        if(key === 'usedInGlobalExceptionHandlig'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((globalException, idx) => {   
                        return (
                            <li key={'globalError' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}>
                                        </span>{globalException.name}</div>
                            </li>
                        )
                    })}
                </ul>
            );
        }

        return lists;
        
    }

    renderModuleUsedIn(mod, serialized){
        const parentMod = (this.props.isComponentNode || serialized.isCommonModule) ? mod : serialized.components.modules.find(m => m.id === mod.props.parentComponentID);

        if (!parentMod) {
            return (
            <this.Panel isError>
                <h4 style={{ marginTop: '0' }}>Parent module error</h4>
                <p>Please try reinstalling or updating this module</p>
            </this.Panel>
            )
        }

        const modUsedIn = ModuleUsedIn.getModuleUsedIn(parentMod, serialized);
        const moduleIsUnused = Object.values(modUsedIn).filter(val => val.length > 0).length === 0;
        const panel = [];
        
        if(moduleIsUnused){
            return (
                <this.Panel isError>
                    <h4 style={{ marginTop: '0' }}>Unused module</h4>
                </this.Panel>
            )
        }
        
        if(modUsedIn['usedInFlows'].length > 0){
            // returns unique values in filtered array
            const getUnique = (val, idx, self) => {
                return self.indexOf(val) === idx;
            }
            // filter array into unique values
            const unique = modUsedIn['usedInFlows'].filter(getUnique)
            
            panel.push(this.getModuleUsedInPanel('usedInFlows', unique, serialized));
        }
        if (modUsedIn['usedInObjects'].length > 0) {
            panel.push(this.getModuleUsedInPanel('usedInObjects', modUsedIn, serialized));
        }
        if(modUsedIn['usedInExitNodes'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInExitNodes', modUsedIn, serialized));
        }

        if(modUsedIn['usedInGlobalIntents'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInGlobalIntents', modUsedIn, serialized));
        }
        
        if(modUsedIn['usedInGlobalErrorHandlig'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInGlobalErrorHandlig', modUsedIn, serialized));
        }

        if(modUsedIn['usedInGlobalExceptionHandlig'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInGlobalExceptionHandlig', modUsedIn, serialized));
        }

        if(modUsedIn['usedInModuleInputs'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInModuleInputs', modUsedIn, serialized));
        }
        if(modUsedIn['usedInLocalVariables'].length > 0){
            panel.push(this.getModuleUsedInPanel('usedInLocalVariables', modUsedIn, serialized));
        }
        
        return panel;
    }

    // OBJECTS --
    getObjectUsedInPanel(usedIn, type, serialized){
        if (!usedIn.length) {
            return null;
        } else if (type === 'inUse') {
            return <UsedInPanels key={type} items={usedIn} />;
        }

        const usedInPanelsMap = {
            modules: {
                title: 'Modules',
                getListFn: this.getObjectUsedInModulesList.bind(this),
            },
            objects: {
                title: 'Application Objects',
                getListFn: this.getUsedInObjectsList.bind(this),
            },
            inGlobalIntent: {
                title: 'Module Inputs',
                getListFn: this.getObjectUsedInGlobalIntentsList.bind(this),
            },
            inLocalVariable: {
                title: 'Local Variables',
                getListFn: this.getObjectUsedInLocalVariablesList.bind(this),
            },
            inModuleInputs: {
                title: 'Global Intents',
                getListFn: this.getObjectUsedInModuleInputsList.bind(this),
            }
        };

        const panel = usedInPanelsMap[type]

        return panel && (
            <this.Panel key={type}>
                 <h4 style={{marginTop: '0'}}>{panel.title}</h4>
                 {panel.getListFn(usedIn, serialized.components)}
            </this.Panel>
        )
    }

    Panel({isError, children}) {
        return (
            <div style={{padding: '1.2em', borderRadius: 'var(--radius-small)', border: `1px solid ${isError ? 'var(--theme-error)' : 'var(--theme-text-emphasis)'}`, marginBottom: '1.2em'}}>
                {children}
            </div>
        );

    } 
    
    getObjectUsedInModulesList(usedInModules, components) {
        return (
            <ul style={{listStyleType: 'none', padding: '0'}}>
                {usedInModules.map((modId, idx) => {   
                    const mod = components.modules.find(mod => mod.id === modId);
                    return (
                        <li key={'objUsedInModules' + idx} style={{padding: '4px 0'}}>
                            <div><span style={{paddingRight: '4px'}}><i className="fe icon-package"></i></span>
                            <a onClick={() => this.openCanvas(mod)}>
                                <span style={{textDecoration: 'underline'}}>{mod.name}
                            </span>
                            </a>
                            </div>
                        </li>
                    )
                })}
            </ul>
        );
    }
    getObjectUsedInLocalVariablesList(usedInLocalVariables) {
        const lists = [];
        usedInLocalVariables.forEach(item => {
            lists.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-hash'} style={{ padding: '0 4px' }}></i><a onClick={() => this.openCanvas(item.mod)}>{`${item.variable.name} in ${item.mod.name} (Module)`}</a></div>)
        });

        return lists;
    }
    getObjectUsedInModuleInputsList(usedInModuleInputs) {
        const lists = [];
        usedInModuleInputs.forEach(item => {
            lists.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-package'} style={{ padding: '0 4px' }}></i><a onClick={() => this.openCanvas(item.mod)}><span style={{textDecoration: 'underline'}}/>{`${item.nod.name} in ${item.mod.name} (Module)`}</a></div>)
        });

        return lists;
    }

    getUsedInObjectsList(usedInObjects, components) {
        const lists = [];
        
        lists.push(<ul style={{listStyleType: 'none', padding: '0'}}>
                    {usedInObjects.map((objId, idx) => {   
                        const obj = components.objects.find(obj => obj.id === objId);
                        const objIcon = obj.props.iconClass
                        const isReferenceableString = (obj.props.config.isReferenceable) ? '(is Referenceable)': '';
                        return (    
                            <li key={'objUsedInObjects' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}>
                                        <i className={"fe icon-" + objIcon}></i>
                                    </span>
                                    <a onClick={() => {
                                        window.apm.openPropertyPanel(obj);
                                        w2popup.close();
                                    }}>
                                        <span>{`${obj.name} ${isReferenceableString}`}</span>
                                    </a> 
                                </div>
                            </li>
                        )
                    })}
                </ul>);

        return lists;
    }

    getObjectUsedInGlobalIntentsList(usedInGlobalIntents, components) {
        const lists = [];
        usedInGlobalIntents.forEach(item => {
            var conditionName = item.gi.conditionProperties.props.config.name ? item.gi.conditionProperties.props.config.name: item.gi.conditionProperties.props.config.serializedCondition;
            var intentName = components.intents.find(int => int.id === item.gi.intent).name ;
            if (conditionName !== "") lists.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i>{`${intentName} + ${conditionName}`}</div>)
            else lists.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i>{`${intentName}`}</div>)
        });

        return lists;
    }

    renderObjectUsedIn(obj, serialized){
        const parentObj = this.props.isComponentNode
            ? obj
            : serialized.components.objects.find((o) => o.id === obj.props.parentComponentID)
    
        if (!parentObj) {
            return (
            <this.Panel isError>
                <h4 style={{ marginTop: '0' }}>Parent module error</h4>
                <p>Please try reinstalling or updating this module</p>
            </this.Panel>
            )
        }
    
        const usedIn = ObjectsUsedIn.getObjectUsedIn(parentObj.id, serialized)
        const usedInKeys = [
            'modules',
            'objects',
            'inGlobalIntent',
            'inLocalVariable',
            'inModuleInputs',
            'inUse'
        ];

        if (!usedIn.some(x => x.length)) {
            return (
                <this.Panel isError>
                    <h4 style={{ marginTop: '0' }}>Unused Object</h4>
                </this.Panel>
            );
        }
    
        return (
            <>
                {usedIn.map((curr, idx) => {
                    return this.getObjectUsedInPanel(curr, usedInKeys[idx], serialized);
                })}
            </>
        )
    }

    // INTENTS --
    getIntentUsedInPanel(key, usedIn, components){
        return (
            <this.Panel key={createUuid()}>
                {this.getIntentUsedInHeader(key)}
                {this.getIntentUsedInList(key, usedIn, components)}
            </this.Panel>
        )
    }

    getIntentUsedInHeader(headerKey){
        const headerText =  (headerKey === 'intentsUsedInCompoundIntents') ? "Compound Intents":
                            (headerKey === 'intentsUsedInFlowLinks') ? "Diagram Transitions":
                            (headerKey === 'intentsUsedInDtmfNodes') ? "DTMF Nodes":
                            (headerKey === 'intentsUsedInInputNodes') ? "Input Nodes":
                            (headerKey === 'intentsUsedInCustomNodes') ? "Custom Nodes":
                            (headerKey === 'intentsUsedInFunctions') ? "Functions":
                            (headerKey === 'intentsUsedInGlobalIntents') ? "Global Intents": '';

        const headerIcon =  (headerKey === 'intentsUsedInCompoundIntents') ? "radio":
                            (headerKey === 'intentsUsedInFlowLinks') ? "link":
                            (headerKey === 'intentsUsedInDtmfNodes') ? "align-justify":
                            (headerKey === 'intentsUsedInInputNodes') ? "mic":
                            (headerKey === 'intentsUsedInCustomNodes') ? "cpu":
                            (headerKey === 'intentsUsedInFunctions') ? "server":
                            (headerKey === 'intentsUsedInGlobalIntents') ? "radio": '';                            

        return <h4 style={{marginTop: '0'}}><span style={{paddingRight: '4px'}}><i className={'fe icon-' + headerIcon}></i></span>{headerText}</h4>
    }

    getIntentUsedInList(key, usedIn, serialized){
        const lists = [];
        const {components} = serialized;

        if(key === 'intentsUsedInInputNodes'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ref, idx) => {   
                        return (
                            <li key={'ref' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    {ref.node.name}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInDtmfNodes'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ref, idx) => {   
                        return (
                            <li key={'ref' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    {ref.node.name}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInCustomNodes'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ref, idx) => {   
                        return (
                            <li key={'ref' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    {ref.node.name}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInFunctions'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ref, idx) => {   
                        return (
                            <li key={'ref' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    {ref.function.name}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInCompoundIntents'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ci, idx) => {   
                        const fromIntent1 = components.intents.find(int => int.id === ci.compoundIntent.fromIntent1);
                        const fromIntent2 = components.intents.find(int => int.id === ci.compoundIntent.fromIntent2);
                        const toIntent = components.intents.find(int => int.id === ci.compoundIntent.toIntent);
                        return (
                            <li key={'ci' + idx} style={{margin: '4px 0', backgroundColor: 'color-mix(in srgb, var(--gray) 12%, transparent)', borderRadius: 'var(--radius-small)', padding: '1em'}}>
                                <div>
                                    <div style={{display: 'flex'}}>
                                        <div style={{margin: '0 1px', flex: 1, textAlign: 'center'}}><span style={{fontWeight: 'bold'}}>From Intent 1</span></div>
                                        <div style={{margin: '0 1px', flex: 1, textAlign: 'center'}}><span style={{fontWeight: 'bold'}}>From Intent 2</span></div>
                                    </div>
                                    <div style={{display: 'flex'}}>
                                        <div style={{padding: '2px', flex: 1, textAlign: 'center'}}>{fromIntent2.name || 'unassigned'}</div>
                                    </div>
                                    <div style={{textAlign: 'center'}}>
                                        <div style={{margin: '0 1px'}}><span style={{fontWeight: 'bold'}}>To Intent</span></div>
                                    </div>
                                    <div style={{textAlign: 'center'}}>
                                        <div style={{padding: '2px'}}>{toIntent.name || 'unassigned'}</div>
                                    </div> 
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInFlowLinks'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((ref, idx) => {   
                        return (
                            <li key={'ref' + idx} style={{padding: '4px 0'}}>
                                <div>
                                <span style={{paddingRight: '4px'}}><i className="fe icon-package"></i></span>
                                <a onClick={() => this.openCanvas(components.modules.find(mod => mod.id === ref.module))}><span style={{textDecoration: 'underline'}}>{components.modules.find(mod => mod.id === ref.module).name}</span></a>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            );
        }
        if(key === 'intentsUsedInGlobalIntents'){
            lists.push(
                <ul style={{listStyleType: 'none', padding: '0'}} key={createUuid()}>
                    {usedIn[key].map((globalIntent, idx) => {   
                        var conditionName = globalIntent.conditionProperties.props.config.name ? globalIntent.conditionProperties.props.config.name: globalIntent.conditionProperties.props.config.serializedCondition;
                        var intentName = components.intents.find(int => int.id === globalIntent.intent).name;
                        if (conditionName !== "") {
                            return (
                            <li key={'globalIntent' + idx} style={{padding: '4px 0'}}>
                                <div>
                                    <span style={{paddingRight: '4px'}}>
                                    <i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i></span>{`${intentName} + ${conditionName}`}</div>
                            </li>
                            )
                        }
                        else {
                            return (
                                <li key={'globalIntent' + idx} style={{padding: '4px 0'}}>
                                    <div>
                                        <span style={{paddingRight: '4px'}}>
                                        <i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i></span>{`${intentName}`}</div>
                                </li>
                            )
                        }
                    })}
                </ul>
            );
        }
        
        return lists;
    }

    renderIntentUsedIn(intent, serialized){
        const intentUsedIn = IntentUsedIn.getIntentUsedIn(intent, serialized);
        const intentIsUnused = Object.values(intentUsedIn).filter(val => val.length > 0).length === 0;
        const panel = [];
        if(intentIsUnused){
            panel.push(<div>Unused Intent</div>)
        }
        else {
            // intentsUsedInDtmfNodes
            if(intentUsedIn['intentsUsedInDtmfNodes'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInDtmfNodes', intentUsedIn, serialized));
            }
            // intentsUsedInInputNodes
            if(intentUsedIn['intentsUsedInInputNodes'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInInputNodes', intentUsedIn, serialized));
            }
            // intentsUsedInCustomNodes
            if(intentUsedIn['intentsUsedInCustomNodes'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInCustomNodes', intentUsedIn, serialized));
            }
            // intentsUsedInFunctions
            if(intentUsedIn['intentsUsedInFunctions'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInFunctions', intentUsedIn, serialized));
            }
            // intentsUsedInCompoundIntents
            if(intentUsedIn['intentsUsedInCompoundIntents'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInCompoundIntents', intentUsedIn, serialized));
            }
            // intentsUsedInFlowLinks
            if(intentUsedIn['intentsUsedInFlowLinks'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInFlowLinks', intentUsedIn, serialized));
            }
            // intentsUsedInGlobalIntents
            if(intentUsedIn['intentsUsedInGlobalIntents'].length > 0){
                panel.push(this.getIntentUsedInPanel('intentsUsedInGlobalIntents', intentUsedIn, serialized));
            }
           
        }

        return panel;
    }

    // FUNCTIONS --
    getFunctionUsedInPanel(key, usedIn) {
        return (
          <this.Panel key={createUuid()}>
            {this.getFunctionUsedInHeader(key)}
            {this.getFunctionUsedInList(usedIn)}
          </this.Panel>
        )
      }
    
      getFunctionUsedInHeader(headerKey) {
        const header = {
          customNodes: { text: 'Custom Scripts', icon: 'cpu' },
          links: { text: 'Transitions', icon: 'link' },
        }[headerKey]
    
        return header ? (
          <h4 style={{ marginTop: '0' }}>
            <span style={{ paddingRight: '4px' }}>
              <i className={'fe icon-' + header.icon}></i>
            </span>
            {header.text}
          </h4>
        ) : (
          <></>
        )
      }
    
      getFunctionUsedInList(usedIn) {
        const getUsedInName = (obj) => {
          if (obj.type === 'object') return obj.name;
          const name = obj.props.config.name || 'UN_NAMED'
          const source = _.get(obj, ['sourcePort', 'parentNode', 'name'])
          const target = _.get(obj, ['targetPort', 'parentNode', 'name'])
    
          return `${name}${source ? ` from ${source}` : ''}${ target ? ` to ${target}` : ''}`
        }
        return (
          <ul style={{ listStyleType: 'none', padding: '0' }} key={createUuid()}>
            {usedIn.map((use) => {
              return (
                <li key={use.id} style={{ padding: '4px 0' }}>
                  <div>{getUsedInName(use)}</div>
                </li>
              )
            })}
          </ul>
        )
      }

      renderFunctionUsedIn(objectFunction, serialized) {
        const functionUsedIn = FunctionsUsedIn.getFunctionUsedIn(objectFunction, serialized)
        const usedIn = Object.entries(functionUsedIn).reduce(
          (acc, [key, value]) =>
            value.length
              ? acc.concat(this.getFunctionUsedInPanel(key, value))
              : acc,
          []
        )
        if (!usedIn.length) return <div>Unused Function</div>;
        return usedIn;
      }

    // VARIABLES --
    getVariableUsedInPanel(type, usedIn, components){
        const varList = (type === 'modRef') ? usedIn.filter(u => u.type === 'inFlowNode' || u.type === 'inFlowLink') :
                        (type === 'applObjectRef') ? usedIn.filter(u => u.type === 'inApplicationObjectConfig' || u.type === 'inApplicationObjectConfigFunctionScript') : 
                        (type === 'inGlobalIntent') ? usedIn.filter(u => u.type === 'inGlobalIntent') :  (type === 'inAppProperties') ? usedIn.filter(u => u.type === 'inAppProperties'):[];
        const listUI = (varList.length === 0) ? [] : (type === 'modRef') ?  this.getVariablesSubGroup(type, varList, components) : this.getVariableUsedInItems(type, varList, components);

        if(listUI.length === 0){
            return;
        }
        
        return (
            <div style={{padding: '1.2em', borderRadius: 'var(--radius-small)', border: '1px solid var(--theme-text-emphasis)', marginBottom: '1.2em'}} key={createUuid()}>
                {this.getVariableUsedInHeader(type, usedIn)}
                {listUI}
            </div>
        )
    }

    getVariableUsedInHeader(type, usedIn){
        const headerText = (type === 'modRef') ? "Modules" : (type === 'applObjectRef') ? "Application Objects" : (type === 'inGlobalIntent') ? 'Global Intents' :(type === 'inAppProperties') ? 'App Properties': ''
        return <h4 style={{marginTop: '0'}} key={createUuid()}><span style={{paddingRight: '4px'}}><i className='fe icon-'></i></span>{headerText}</h4>
    }
    getVariablesSubHeader(mod){
        return <div key={createUuid()}><h6><i className="fe icon-package" style={{paddingRight: '4px'}}></i><a onClick={() => this.openCanvas(mod)}>{mod.name}</a></h6></div>
    }

    getVariablesSubGroup(type, varList, components){
        return (
            <div style={{margin: '7px'}} key={createUuid()}>
                {this.getVariableUsedInList(type, varList, components)}
            </div>
        );
    }

    getVariableUsedInList(type, usedIn, components){
        const list = [];
        
        if(type === 'modRef'){
            const mods = new Set(usedIn.map(u => u.mod.id));
            mods.forEach(m => {
                const modArr = usedIn.filter(u => u.mod.id === m);
                list.push(
                    <div style={{marginBottom: '10px'}} key={createUuid()}>
                        {this.getVariablesSubHeader(modArr[0].mod)}
                        {this.getVariableUsedInItems(type, modArr, components)}
                    </div>
                );
            });
        }
        if(type === 'applObjectRef'){
            list.push(<div key={createUuid()}>{this.getVariableUsedInItems(type, usedIn, components)}</div>)
        }

        return list;            
    }

    getVariableUsedInItems(type, usedInItems, components){
        const itemsList = [];
        if(type === 'modRef'){
            usedInItems.forEach(item => {
                if(item.node){
                    const iconClass = "fe icon-" + item.node.props.iconClass;
                    const setGetLabel = item.isSetApp && item.isGetApp ? "(set/get)" : ( item.isSetApp ? "(set)" : (item.isGetApp ? "(get)" : "") );
                    const nodeName = `${item.node.name} ${setGetLabel}`;
                    itemsList.push(<div style={{padding: '2px 0'}}><i className={iconClass}style={{padding: ' 0 4px'}} key={createUuid()}></i>{nodeName}</div>)
                }

                if(item.link) {
                    const iconClass = "fe icon-link";
                    const linkName = item.link.props.config.name || 'Un named';
                    itemsList.push(<div style={{padding: '2px 0'}}><i className={iconClass}style={{padding: ' 0 4px'}} key={createUuid()}></i>{linkName}</div>)
                }
            });
        }
        if(type === 'applObjectRef'){
            usedInItems.forEach(item => {
                const iconClass = "fe icon-" + item.obj.props.iconClass;
                const setGetLabel = item.isSetApp && item.isGetApp ? "(set/get)" : ( item.isSetApp ? "(set)" : (item.isGetApp ? "(get)" : "") );
                const nodeName = `${item.obj.name} ${setGetLabel}`;

                itemsList.push(
                    <div 
                        style={{padding: '2px 0'}} 
                        key={createUuid()} >
                        <i 
                            className={iconClass} 
                            style={{padding: '0 4px'}}>
                        </i>{nodeName}
                    </div>);
                });

        }
        if(type === 'inGlobalIntent'){
            usedInItems.forEach(item => {
                var conditionName = item.gi.conditionProperties.props.config.name ? item.gi.conditionProperties.props.config.name: item.gi.conditionProperties.props.config.serializedCondition;
                var intentName = components.intents.find(int => int.id === item.gi.intent).name ;
                if (conditionName !== "") itemsList.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i>{`${intentName} + ${conditionName}`}</div>)
                else itemsList.push(<div style={{ padding: '2px 0' }} key={createUuid()}><i className={'fe icon-radio'} style={{ padding: '0 4px' }}></i>{`${intentName} + ${conditionName}`}</div>)
            });

        }
        if(type === 'inAppProperties'){
            usedInItems.forEach(item => {
                itemsList.push(<div style={{padding: '2px 0'}} key={createUuid()}>{item.appProp.config}</div>)
            });

        }

        return itemsList;
    }

    renderVariableUsedIn(variable, serialized){
        const usedIn = VariablesUsedIn.getVariableUsedIn(variable, serialized);
        const variableIsUnused = usedIn.length === 0;
        const {components,appProperties} = serialized;
        const panel = [];

        if(variableIsUnused){
            panel.push(<div key={createUuid()}>Unused Variable</div>)
        }
        else {
            panel.push(this.getVariableUsedInPanel('modRef', usedIn, components))
            panel.push(this.getVariableUsedInPanel('applObjectRef', usedIn, components))
            panel.push(this.getVariableUsedInPanel('inGlobalIntent', usedIn, components))
            panel.push(this.getVariableUsedInPanel('inAppProperties', usedIn, appProperties))
        }
        return panel;
    }
    renderLocalVariableUsedIn(variable, module, serialized){
        const usedIn = LocalVariablesUsedIn.getVariableUsedIn(variable, module, serialized);
        const variableIsUnused = usedIn.length === 0;
        const {components} = serialized;
        const panel = [];

        if(variableIsUnused){
            panel.push(<div key={createUuid()}>Unused Variable</div>)
        }
        else {
            panel.push(this.getVariableUsedInPanel('modRef', usedIn, components))
            panel.push(this.getVariableUsedInPanel('applObjectRef', usedIn, components))
            panel.push(this.getVariableUsedInPanel('inGlobalIntent', usedIn, components))
        }
        return panel;
    }


    /**
     * Render references list
     * @param {object} usedIn
     * @returns {Array}
     */
    renderReferences(usedIn) {
        const elements = [];
        for (var type in usedIn) {
            if (usedIn.hasOwnProperty(type) && type !== "count") {
                const typeElements = [];
                var id = createUuid();
                if (type !== 'compound_intents') {
                    for (var i = 0; i < usedIn[type].length; i++) {
                        const elem = usedIn[type][i];
                        typeElements.push(
                            <div key={id + i}>
                                {(() => {
                                    if (elem && elem.location && elem.location === "-on transition)") {
                                        return <div><i style={{ paddingRight: "2px" }} className={"fe icon-" + elem.iconClass} /> <a onClick={() => this.openCanvas(elem)}>{elem.name + elem.location}</a></div>;
                                    }
                                    else if (elem && elem.type === "module") {
                                        return <div><i style={{ paddingRight: "2px" }} className={"fe icon-" + elem.iconClass} /> <a onClick={() => this.openCanvas(elem)}>{elem.name}</a></div>;
                                    }
                                    else if (elem && elem.location && elem.type === "Data Access") {
                                        return <div> <i style={{ paddingRight: "2px" }} className={"fe icon-" + elem.iconClass} /> <span>{elem.name + " (" + window.l.popup[elem.type] + " in " + elem.location + " module)"}</span></div>;
                                    }
                                    else {
                                        return <div><i style={{ paddingRight: "2px", }} className={"fe icon-" + elem.iconClass} /> <span>{elem.name}</span></div>;
                                    }
                                })()}
                            </div>
                        );
                    }
                }
                else if (type === 'compound_intents') {
                    for (var i = 0; i < usedIn[type].length; i++) {
                        const com = usedIn[type][i];
                        typeElements.push(

                            <div key={createUuid()}>
                                {(() => {
                                    return (<table style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th>From Intent 1</th>
                                                <th>From Intent 2</th>
                                                <th>To Intent</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr >
                                                <td>{com.fromIntent1 ? com.fromIntent1.name : "???"}</td>
                                                <td>{com.fromIntent2 ? com.fromIntent2.name : "???"}</td>
                                                <td>{com.toIntent ? com.toIntent.name : "???"}</td>
                                            </tr>
                                        </tbody>
                                    </table>)
                                })()} </div>
                        )
                    }

                }

                if (usedIn[type].length === 0) {
                    continue;
                }
                elements.push(
                    <div className={"card"} key={id}>
                        <div>
                            <h5>
                                <a className={"primary-title"} data-toggle={"collapse in"}
                                    aria-controls={id} aria-expanded={"false"}>
                                    {usedIn[type].length + (usedIn[type].length > 1 ? window.l.popup.space_references : window.l.popup.space_reference) + window.l.popup.space_used_in_space + window.l.popup.type_references[type]}
                                </a>
                            </h5>
                        </div>

                        <div id={id} className={"collapse in"} aria-expanded={"false"} href={"#" + id}>
                            <div>
                                {typeElements}
                            </div>
                        </div>
                    </div>
                );
            }
        }
        return elements;
    }

    /**
     * React render
     * @returns {XML}
     */
    render() {
        //const serialized = window.apm.getSerializedFromNode(this.props.node);
        
        if(this.props.node.type === 'module'){
            return this.renderModuleUsedIn(this.props.node, window.apm.serialized)    
        }
        if(this.props.node.type === 'intent'){
            return this.renderIntentUsedIn(this.props.node, window.apm.serialized)
        }
        if(this.props.node.hasOwnProperty('script')){
            return this.renderFunctionUsedIn(this.props.node, window.apm.serialized)
        }
        if(this.props.node.type === 'object'){
            return this.renderObjectUsedIn(this.props.node, window.apm.serialized)
        }
        if(this.props.node.type === 'variable'&!this.props.isLocalVariable){
            return this.renderVariableUsedIn(this.props.node, window.apm.serialized)
        }
        if(this.props.node.type === 'variable'&this.props.isLocalVariable){
            return this.renderLocalVariableUsedIn(this.props.node, this.props.module, window.apm.serialized)
        }
        else{
            return (<h5>{`${this.props.node.type.toUpperCase()} not handled`}</h5>)
        }
    }

}
